import {openAiActionLoadingModal} from '@Modals/ai-action-loading-modal';
import {getMakeTextEditableLayersVisualConfig, getMakeTextEditableLoadingTexts} from './make-text-editable-layers-visual';
import type {PublishingOption} from '@Components/ai-chat-window/components/publishing-options/publishing-options.types';
import {applyLetterCase, getUniqueString, LetterCase} from '@Utils/string.util';
import type {AiActionLoadingModalProps, NavigationAction} from '@Modals/ai-action-loading-modal/ai-action-loading-modal.types';
import {AiActionVisualKind} from '@Modals/ai-action-loading-modal/ai-action-loading-modal.types';
import {LayeringType} from '@Libraries/layering-type';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {trackFakeDoorPostUsedIfNeeded} from '@Libraries/marketing-planner.library';
import {getPublishingCopy} from '@Components/ai-chat-window/lib/publish-copy.library';
import {getMdeLoadingTexts} from '@Libraries/make-design-editable.library';
import {getMdeLayersVisualConfig} from '@Libraries/make-design-editable-layers-visual.library';

const liveSessionIds = new Set<string>();

const startModalSession = (): string => {
  const sessionId = getUniqueString();
  liveSessionIds.add(sessionId);

  return sessionId;
};

export const isCurrentModalSession = (sessionId: string): boolean => {
  return liveSessionIds.has(sessionId);
};

export const invalidateModalSession = (): void => {
  liveSessionIds.clear();
};

export const openEditorLoadingModalAndGetSessionId = (): string => {
  const sessionId = startModalSession();
  openAiActionLoadingModal({
    title: applyLetterCase(window.i18next.t('pmwjs_edit_image_in_editor'), LetterCase.SENTENCE_CASE),
    loadingText: window.i18next.t('pmwjs_preparing_editor'),
    completedText: window.i18next.t('pmwjs_design_is_ready'),
    ctaText: window.i18next.t('pmwjs_edit_image_in_editor'),
  });

  return sessionId;
};

export const showEditorReadyModal = (navigationUrl: string): void => {
  openAiActionLoadingModal({
    title: applyLetterCase(window.i18next.t('pmwjs_edit_image_in_editor'), LetterCase.SENTENCE_CASE),
    loadingText: window.i18next.t('pmwjs_preparing_editor'),
    completedText: window.i18next.t('pmwjs_design_is_ready'),
    ctaText: window.i18next.t('pmwjs_edit_image_in_editor'),
    navigationAction: {
      url: navigationUrl,
      openInNewTab: true,
    },
  });
};

export const openMakeTextEditableLoadingModalAndGetSessionId = (layeringType: LayeringType = LayeringType.TEXT): string => {
  const sessionId = startModalSession();
  openAiActionLoadingModal({
    title: applyLetterCase(window.i18next.t('pmwjs_make_text_editable'), LetterCase.SENTENCE_CASE),
    loadingText: window.i18next.t('pmwjs_extracting_editable_text'),
    completedText: window.i18next.t('pmwjs_editable_design_is_ready'),
    ctaText: window.i18next.t('pmwjs_open_in_editor'),
    visual: {kind: AiActionVisualKind.LAYERS, layers: getMakeTextEditableLayersVisualConfig(layeringType)},
    loadingTexts: getMakeTextEditableLoadingTexts(),
  });

  return sessionId;
};

export const showMakeTextEditableReadyModal = (navigationUrl: string): void => {
  openAiActionLoadingModal({
    title: applyLetterCase(window.i18next.t('pmwjs_make_text_editable'), LetterCase.SENTENCE_CASE),
    loadingText: window.i18next.t('pmwjs_extracting_editable_text'),
    completedText: window.i18next.t('pmwjs_mte_layers_ready'),
    ctaText: window.i18next.t('pmwjs_open_in_editor'),
    navigationAction: {
      url: navigationUrl,
      openInNewTab: true,
      onBeforeNavigate: trackFakeDoorPostUsedIfNeeded,
    },
    visual: {kind: AiActionVisualKind.LAYERS, layers: getMakeTextEditableLayersVisualConfig()},
  });
};

export const openMdeLoadingModalAndGetSessionId = (): string => {
  const sessionId = startModalSession();
  openAiActionLoadingModal({
    ...getMdeLoadingModalCopy(),
    loadingTexts: getMdeLoadingTexts(),
  });

  return sessionId;
};

export const showMdeReadyModal = (navigationUrl: string): void => {
  openAiActionLoadingModal({
    ...getMdeLoadingModalCopy(),
    navigationAction: {
      url: navigationUrl,
      openInNewTab: true,
    },
  });
};

const getMdeLoadingModalCopy = (): AiActionLoadingModalProps => {
  return {
    title: applyLetterCase(window.i18next.t('pmwjs_mde_editable_layers'), LetterCase.SENTENCE_CASE),
    loadingText: window.i18next.t('pmwjs_mde_extracting_layers'),
    completedText: window.i18next.t('pmwjs_editable_design_is_ready'),
    ctaText: window.i18next.t('pmwjs_open_in_editor'),
    visual: {kind: AiActionVisualKind.LAYERS, layers: getMdeLayersVisualConfig()},
  };
};

export const openVideoEditorLoadingModalAndGetSessionId = (): string => {
  const sessionId = startModalSession();
  openAiActionLoadingModal({
    title: applyLetterCase(window.i18next.t('pmwjs_edit_video_in_editor'), LetterCase.SENTENCE_CASE),
    loadingText: window.i18next.t('pmwjs_preparing_video_editor'),
    completedText: window.i18next.t('pmwjs_design_is_ready'),
    ctaText: window.i18next.t('pmwjs_edit_video_in_editor'),
  });

  return sessionId;
};

export const showVideoEditorReadyModal = (navigationUrl: string): void => {
  openAiActionLoadingModal({
    title: applyLetterCase(window.i18next.t('pmwjs_edit_video_in_editor'), LetterCase.SENTENCE_CASE),
    loadingText: window.i18next.t('pmwjs_preparing_video_editor'),
    completedText: window.i18next.t('pmwjs_design_is_ready'),
    ctaText: window.i18next.t('pmwjs_edit_video_in_editor'),
    navigationAction: {
      url: navigationUrl,
      openInNewTab: true,
      onBeforeNavigate: trackFakeDoorPostUsedIfNeeded,
    },
  });
};

export const openShareLoadingModalAndGetSessionId = (): string => {
  const sessionId = startModalSession();

  openAiActionLoadingModal({
    title: applyLetterCase(window.i18next.t('pmwjs_share_design'), LetterCase.SENTENCE_CASE),
    loadingText: window.i18next.t('pmwjs_preparing_share'),
    completedText: window.i18next.t('pmwjs_ready_to_share'),
    ctaText: window.i18next.t('pmwjs_open_sharing_options'),
  });

  return sessionId;
};

export const openPublishingLoadingModalAndGetSessionId = (option: PublishingOption): string => {
  const sessionId = startModalSession();

  const config = getPublishingCopy(option);
  openAiActionLoadingModal({
    title: applyLetterCase(config.title, LetterCase.SENTENCE_CASE),
    loadingText: config.loadingText,
    completedText: config.completedText,
    ctaText: config.ctaText,
  });

  return sessionId;
};

export const showPublishingReadyModal = (option: PublishingOption, navigationAction: NavigationAction): void => {
  const config = getPublishingCopy(option);
  openAiActionLoadingModal({
    title: applyLetterCase(config.title, LetterCase.SENTENCE_CASE),
    loadingText: config.loadingText,
    completedText: config.completedText,
    ctaText: config.ctaText,
    navigationAction,
  });
};

export const closeAiActionLoadingModal = (): void => {
  window.PMW.redux.store.dispatch(closeModal(MODALS_IDS.AI_ACTION_LOADING_MODAL));
  invalidateModalSession();
};
